import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { format } from 'date-fns'
import { Dialog, DialogContent, DialogTrigger } from '../../../../../components/ui/dialog'
import { useWidget } from '../../../../../providers/widget'
import { ExploreWidgetConfig } from '../../../config'
import { useExplorer } from '../../../providers'
import { useExplorerCss } from '../../../hooks/useExplorerCss'
import { useFilterTotalsState, useSearchSubmit, SecondaryRow, isHeroBar, isRedirectMode } from '../searchShared'
import { useSbpLabels } from './labels'
import { useSbpCompact } from './compact'
import { sbpBarMinWidth, useSbpNarrowBar } from './narrowBar'
import { useWhereSections, useDestinationAwareWhereLabel } from './whereSources'
import ProWhereField from './ProWhereField'
import DateFilter from '../DateFilter'
import GuestFilter from '../GuestFilter'
import AdditionalFilters from '../AdditionalFilters'
import BedroomsField from './BedroomsField'
import QuickChips from './QuickChips'
import FlexAltDates from './FlexAltDates'
import { useHeroSticky } from './heroSticky'
import { useRefineSticky } from './refineSticky'
import { useStickyElevation, deepActiveElement } from './stickyElevation'
import { remeasureSbpAnchors } from './anchorBus'

/**
 * Searchbar Pro variant of the explorer search UI (#853): the toolkit SBP
 * look ported onto the widget's existing field dialogs. Desktop (≥933px)
 * renders the pill bar; below 933px (the toolkit's real breakpoint — not
 * 768/960, see the port spec) a two-line trigger pill opens a full-screen
 * staged search sheet. The same swap also runs on the bar's CONTAINER width
 * (#1846, `hfse-sbp-narrow` from useSbpNarrowBar): a desktop viewport whose
 * column cannot hold the fields gets the trigger pill too. The secondary row
 * (Start Over / count / sort / map toggle) is shared with the legacy variant
 * unchanged.
 */

/** Selector for the field that currently owns the active pill. The three
 *  dialog-backed fields are Radix triggers, so Radix stamps them
 *  `data-state="open"`; the Where field is a plain div that flags itself with
 *  `hfse-sbp-field-active`. Both shapes have to be matched or the pill goes
 *  dark on whichever field the visitor opened. */
export const ACTIVE_FIELD_SELECTOR =
  '.hfse-sbp-field-btn[data-state="open"], .hfse-sbp-field.hfse-sbp-field-active'

/**
 * Slides one of the shared indicator pills over `field` (toolkit parity:
 * searchbar-pro.js writes left/width as inline styles and lets CSS spring
 * them, rather than cross-fading a per-field background). Returns the
 * geometry written, or null when there is nothing to point at — an unlaid-out
 * field measures 0 wide, and a zero-width pill would flash at the bar's left
 * edge before the first real measurement lands.
 *
 * Every write is guarded on the value actually changing. That makes the
 * function a fixed point: the MutationObserver that drives it watches the
 * subtree the pill lives in, so an unguarded write would re-enter this
 * function forever (it did — an OOM, not a hang). It also keeps the browser
 * from re-laying-out on observer passes that changed nothing.
 */
export function applyPill(
  pill: HTMLElement | null,
  field: HTMLElement | null,
  visibleClass: string,
): { left: number; width: number } | null {
  if (!pill) return null
  if (!field || field.offsetWidth === 0) {
    if (pill.classList.contains(visibleClass)) pill.classList.remove(visibleClass)
    return null
  }
  const left = field.offsetLeft
  const width = field.offsetWidth
  const nextLeft = `${left}px`
  const nextWidth = `${width}px`
  if (pill.style.left !== nextLeft) pill.style.left = nextLeft
  if (pill.style.width !== nextWidth) pill.style.width = nextWidth
  if (!pill.classList.contains(visibleClass)) pill.classList.add(visibleClass)
  return { left, width }
}

const searchIcon = (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
)

/**
 * Gap and horizontal padding of the EXPANDED submit rule in explorer.css.
 * sizeSubmit restates them in its width arithmetic, and
 * searchbar-motion-css.test.ts pins the stylesheet to these exact values so
 * the two sides cannot drift apart silently (2026-08-05 audit).
 */
export const CTA_GAP_PX = 8
export const CTA_PAD_X_PX = 24

/**
 * Abbreviated, YEAR-LESS date for a summary line. Toolkit contract
 * (searchbar-pro.js:303-313): `Intl.DateTimeFormat(undefined, { month:
 * 'short', day: 'numeric' })` — locale-aware, no explicit pattern, no year,
 * joined with a spaced EN DASH (U+2013).
 *
 * The admin `dateFormat` token is skipped only at its SCHEMA DEFAULT, whose
 * year on both sides is what Eli flagged (QA 2026-08-05 item 3). A site that
 * deliberately set a format still gets it, matching what DateFilter does for
 * the desktop pill: skipping it unconditionally meant #560 had no effect at
 * all below 933px, so one range read "12/08/2026 – 03/09/2026" on desktop and
 * "Aug 12 – Sep 3" on the phone (codex re-pass 2026-08-05, confirmed MEDIUM).
 *
 * Duplicated in DateFilter by design for now — the datepicker lane owns that
 * file on a sibling branch and a shared helper would be a merge conflict.
 * Worth a dedupe once both land; keep the two selection rules identical until
 * then, or the breakpoint split comes straight back.
 */
const DEFAULT_DATE_FORMAT = 'MMM d, yyyy' // config.ts:196 — keep in step

const shortDate = (iso: string, dateFormat?: string): string => {
  const d = new Date(iso + 'T00:00:00')
  if (Number.isNaN(d.getTime())) return ''
  if (dateFormat && dateFormat !== DEFAULT_DATE_FORMAT) {
    try {
      return format(d, dateFormat)
    } catch {
      // An unparseable admin token must not blank the trigger. Fall back to
      // the DEFAULT format rather than to the short form: DateFilter's desktop
      // pill does the same, and falling through to short here would reopen the
      // breakpoint split on exactly the misconfigured sites this branch
      // handles (e.g. a free-text 'YYYY-MM-DD', which date-fns rejects).
      return format(d, DEFAULT_DATE_FORMAT)
    }
  }
  try {
    return new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' }).format(d)
  } catch {
    return d.toDateString().slice(4, 10)
  }
}

function useSearchSummaries() {
  const { state } = useExplorer()
  const { options } = useWidget<ExploreWidgetConfig>()
  const sections = useWhereSections()
  // Destination-aware (#1072 D1): a popup pick writes tags/locations ids the
  // sections cannot resolve; the plain resolver would summarize "Anywhere"
  // over an active destination filter.
  const whereLabel = useDestinationAwareWhereLabel(sections)

  const vf = state.visitorFilters
  const df = options.dateFormat
  const dateLabel = vf.checkin && vf.checkout
    ? `${shortDate(vf.checkin, df)} – ${shortDate(vf.checkout, df)}`
    : vf.checkin ? shortDate(vf.checkin, df) : ''
  const guestTotal = (vf.adults ?? 0) + (vf.children ?? 0)
  // Pets ride as their own pluralized segment, never in the guests total —
  // module summary shape "3 guests, 1 pet" (#932; searchbar-pro.js:5140-5143).
  // Independent segments: a pets-only deep link still reads "1 pet" (module
  // buildGuestLabel does the same), and a hidden pets row never leaks a
  // URL-seeded pets flag into the summary (same gate as GuestFilter).
  const guestSegment = guestTotal > 0 ? `${guestTotal} guest${guestTotal !== 1 ? 's' : ''}` : ''
  const petsSegment = (options.showPetsFilter ?? true) && vf.pets
    ? `${state.petsCount} pet${state.petsCount !== 1 ? 's' : ''}` : ''
  const guestLabel = [guestSegment, petsSegment].filter(Boolean).join(', ')
  return { whereLabel, dateLabel, guestLabel }
}

/** `withSecondaryRow` (#1251): see FilterBar — false on the mobile sheet, where
 *  the results header is mounted once in the sheet head. */
export default function ProFilterBar({ withSecondaryRow = true }: { withSecondaryRow?: boolean } = {}) {
  const { options } = useWidget<ExploreWidgetConfig>()
  const { resetFilters, state } = useExplorer()
  const cssString = useExplorerCss()
  const sb = options.searchbar
  const labels = useSbpLabels()
  const compactBar = useSbpCompact()
  const { total: totalItems, hasData: totalsReady } = useFilterTotalsState()
  const submitSearch = useSearchSubmit()
  const [mobileOpen, setMobileOpen] = useState(false)

  // Which primary inputs to show, and in what order (WP: primary_filter_fields).
  const primaryFields = options.primaryFilters?.length
    ? options.primaryFilters
    : ['where', 'date', 'guests', 'additional-filters']

  // --- Mobile sheet accordion (Eli r5) -------------------------------------
  // The module's staged sheet is an inline ACCORDION: one stage active at a
  // time (`--active`), a header tap activates that stage in place, and the
  // sheet opens on the first rendered stage (searchbar-pro.js setActiveStage
  // ~5115 — tapping the active header keeps it active, nothing collapses to
  // zero). The widget's stage dialogs are gone below 933px; each field now
  // renders its header + body inline and this state is the single source of
  // which stage is expanded.
  const sheetStageList = primaryFields.filter((f) =>
    ['where', 'date', 'guests', 'additional-filters'].includes(f))
  const [sheetStage, setSheetStage] = useState<string>(sheetStageList[0] ?? '')
  // Module parity: a destination pick advances the sheet to the next stage
  // (the same chain the desktop bar runs), opt-out via the same autoAdvance.
  const advanceSheetFrom = (from: string) => {
    if (options.searchbar.autoAdvance === false) return
    const i = sheetStageList.indexOf(from)
    if (i >= 0 && i + 1 < sheetStageList.length) setSheetStage(sheetStageList[i + 1])
  }
  // Footer Next: the module's manual stage advance (hidden on the last stage).
  const sheetStageIdx = sheetStageList.indexOf(sheetStage)
  const sheetNextStage = sheetStageIdx >= 0 && sheetStageIdx + 1 < sheetStageList.length
    ? sheetStageList[sheetStageIdx + 1]
    : null

  // Promoted-filter slot (#922, toolkit #461): DESKTOP BAR fields only — the
  // mobile sheet keeps its stage flow. Inserted before the Filters field
  // (module: between WHO and WHAT); with the Filters field hidden it lands at
  // the bar's end, matching the module's promoted-to-the-edge behavior.
  const barFields: string[] = (() => {
    if (sb.barPromotedFilter !== 'bedrooms') return primaryFields
    const list = [...primaryFields]
    const filtersIdx = list.indexOf('additional-filters')
    list.splice(filtersIdx >= 0 ? filtersIdx : list.length, 0, 'promoted-bedrooms')
    return list
  })()

  // --- Chained popovers (#923, toolkit PR #469 + 985682c) -------------------
  // The module's setActiveField chain, expressed as a request the target field
  // consumes: destination pick → WHEN, completed date range → WHO, guests
  // Done → promoted BEDROOMS when present else the Filters field, bedrooms
  // Done → Filters. Desktop bar only (the mobile stage instances get no
  // callbacks), opt-out via searchbar.autoAdvance (module default ON).
  // HERO-ONLY as of r7 (#936) — EXCEPT the destination pick: the module gates
  // the date/guests/bedrooms advance branches on `!isRefineBar`
  // (searchbar-pro.js:2434, 2831, 2858) — on a refine bar those popovers just
  // close, because the results already refreshed live. But selectItem()'s
  // where→WHEN advance carries NO refine gate (js:3190-3192, `AUTO_ADVANCE &&
  // showDates` only): the refine bar advances there too. r9 cross-check
  // (#934): the where advance keys on the user opt-out alone; the rest stay
  // redirect-gated (hero implies redirect via isRedirectMode).
  const autoAdvanceUser = sb.autoAdvance !== false
  const autoAdvance = autoAdvanceUser && isRedirectMode(options)
  const [advanceReq, setAdvanceReq] = useState<{ field: string; n: number }>({ field: '', n: 0 })
  const advanceFrom = useCallback((from: 'where' | 'date' | 'guests' | 'bedrooms') => {
    if (!(from === 'where' ? autoAdvanceUser : autoAdvance)) return
    const fields = primaryFields
    const next =
      from === 'where' ? (fields.includes('date') ? 'date' : null)
      : from === 'date' ? (fields.includes('guests') ? 'guests' : null)
      : from === 'guests'
        ? (sb.barPromotedFilter === 'bedrooms'
          ? 'promoted-bedrooms'
          : (fields.includes('additional-filters') ? 'additional-filters' : null))
      : from === 'bedrooms' ? (fields.includes('additional-filters') ? 'additional-filters' : null)
      : null
    if (next) setAdvanceReq((prev) => ({ field: next, n: prev.n + 1 }))
    // primaryFields is rebuilt per render but derives from options —
    // referencing it here is stable in practice.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoAdvance, autoAdvanceUser, sb.barPromotedFilter, options.primaryFilters])
  const signalFor = (field: string) => (advanceReq.field === field ? advanceReq.n : 0)

  const { whereLabel, dateLabel, guestLabel } = useSearchSummaries()

  // Mobile trigger summary (toolkit updateSummaries parity): primary line is
  // the first slot with a value (destination → dates → guests), else the
  // start prompt; the secondary line joins the remaining slots as values or
  // their placeholders.
  const mobileSummary = useMemo(() => {
    const slots: Array<{ value: string; placeholder: string; field: string }> = []
    for (const field of primaryFields) {
      // Placeholders are the admin-editable phrases (#935 item 1) — the same
      // options the field pills render, so the trigger summary and the sheet
      // can never disagree about an empty field's wording.
      if (field === 'where') slots.push({ value: whereLabel, placeholder: labels.whereIdle, field })
      else if (field === 'date') slots.push({ value: dateLabel, placeholder: labels.whenIdle, field })
      else if (field === 'guests') slots.push({ value: guestLabel, placeholder: labels.whoIdle, field })
    }
    const first = slots.find((s) => s.value)
    if (!first) return { primary: labels.startPrompt, secondary: slots.map((s) => s.placeholder).join(' · ') }
    const rest = slots.filter((s) => s !== first)
    return {
      primary: first.value,
      secondary: rest.map((s) => s.value || s.placeholder).join(' · '),
    }
  }, [primaryFields, whereLabel, dateLabel, guestLabel, labels.whereIdle, labels.whenIdle, labels.whoIdle, labels.startPrompt])

  // --- Hero scroll-detach (#934, toolkit initSticky hero branch) ------------
  // Only the hero layout detaches. The compressed summary is the module's
  // buildSummary (destination · dates · N guests, else the start prompt),
  // derived from the same state the mobile trigger summary reads instead of
  // scraping the DOM; both sticky variants' pills render it.
  const hero = isHeroBar(options)
  // --- Narrow-container gate (#1846) ----------------------------------------
  // The 932px media swap cannot see the column the bar sits in (shadow root,
  // viewport-only @media); the wrapper below is measured instead and
  // `hfse-sbp-narrow` swaps the same two nodes. Both sticky variants stand
  // down while narrow: the pill is the surface, and a pinned shell lives in
  // the body portal, out of the wrapper's selector reach. See narrowBar.ts.
  const wrapperRef = useRef<HTMLDivElement>(null)
  const narrowThreshold = sbpBarMinWidth({ fields: barFields, compact: compactBar.compact, hero })
  const narrow = useSbpNarrowBar(wrapperRef, narrowThreshold, sb.maxWidthPx)
  const heroStickyOn = hero && options.search?.heroSticky !== false
  // The header offset also moves the PIN BOUNDARY (#1220 item 3): pinning
  // at the viewport top while the pinned surface sits 16px+offset lower let
  // the bar slide under a fixed host header and then pop down.
  const stickyTopOffsetPx = options.search?.heroStickyTopPx ?? 0
  const heroSticky = useHeroSticky(heroStickyOn && !narrow, stickyTopOffsetPx)
  const heroCompressed = heroSticky.isSticky && !heroSticky.isExpanded
  const compressedSummary =
    [whereLabel, dateLabel, guestLabel].filter(Boolean).join(' · ') || labels.startPrompt

  // --- Hero containment elevation (#951) ------------------------------------
  // `.hfse-sbp-hero-shell-sticky` and the backdrop are position:fixed; a host
  // ancestor that establishes a containing block for fixed elements re-roots
  // them, so the anatomy renders into a stable portal container re-parented
  // to the widget-core elevated body portal while pinned — mechanism and
  // rationale in stickyElevation.ts (shared with the refine bar, #1259). The
  // hero dress (64px fields/CTA, explorer.css `.hfse-explorer-hero
  // .hfse-sbp-field`) is keyed on the container class the shell leaves
  // behind, so the elevated container re-carries it.
  const heroElevation = useStickyElevation({
    enabled: heroStickyOn,
    isSticky: heroSticky.isSticky,
    elevatedClassName: 'hfse-explorer-hero',
    cssString,
  })

  // --- Refine in-place sticky (#1092, toolkit initSticky refine branch) -----
  // The integrated (non-hero) bar is the module's refine bar: under the same
  // top-level stickyFilter knob the legacy bar pins with, the pro bar now
  // pins BAR-ONLY in place over the results column and condenses to the
  // centered pill on a scroll threshold (module v3.67.0 + v3.68.1) — the
  // behavior upgrade replaces the legacy whole-block opaque pin for this
  // variant (explorer.css scopes that rule to :not(.hfse-sbp)). The module's
  // behavior IS the spec (#1092); no new knob.
  //
  // DELIBERATE (#1092 review): hero + stickyFilter + `search.heroSticky:
  // false` gets NO pin of any kind. The hero bar's only pin is the hero
  // detach; an operator who explicitly disabled it opted the hero bar out of
  // pinning, and stickyFilter must not resurrect a pin behind their back
  // (pre-#1092 the container class whole-block-pinned this combo — that was
  // the accident, not the contract).
  const refineStickyOn = !hero && !!options.stickyFilter
  // Live-read by the hook's armed listeners (updated synchronously at the
  // activation sites below, not on re-render): while a field/popover is
  // open, scroll compression freezes and Esc closes the inner surface only.
  const hasActiveFieldRef = useRef(false)
  const refineSticky = useRefineSticky(refineStickyOn && !narrow, () => hasActiveFieldRef.current, stickyTopOffsetPx)
  // The hook guarantees isCompressed implies isSticky (updateCompress is
  // gated on the sticky ref; exitSticky clears both in one commit).
  const refineCompressed = refineSticky.isCompressed

  // --- Refine containment elevation (#1259) ---------------------------------
  // Same hostile-host class as #951, on the in-place shell: position:fixed
  // under a transformed/filtered ancestor pinned to the ancestor and scrolled
  // away with the page, and the click-catcher covered only the ancestor
  // (hostile bench: shell at viewport top -302px instead of 12px). The shell
  // + backdrop ride the same re-parented container; the sentinel and spacer
  // stay in the slot, so positionRefine (spacer rect -> shell left/width) and
  // the compress threshold (sentinel rect) keep reading in-tree geometry —
  // viewport coordinates that are correct precisely because the shell now
  // sits at body level. No extra dress class: the refine bar's pinned look
  // is keyed on the shell classes, and the --hfse-sbp-* tokens reach the
  // elevated scope through searchbarCss.
  const refineElevation = useStickyElevation({
    enabled: refineStickyOn,
    isSticky: refineSticky.isSticky,
    elevatedClassName: '',
    cssString,
  })

  // #1220 item 1: a pin/unpin (and the compress/expand morph) moves the bar
  // AFTER the scroll event that triggered it already ran the portal anchors'
  // own measurements, so an open popover or listbox held stale coordinates
  // until the next scroll pixel. Re-measure both anchor systems right after
  // the sticky DOM commits — layout effect, so geometry is final and nothing
  // has painted; declared after both elevation hooks above (#951 hero,
  // #1259 refine) so the elevated shell is already in its new home when
  // measured.
  useLayoutEffect(() => {
    remeasureSbpAnchors()
  }, [heroSticky.isSticky, heroSticky.isExpanded, refineSticky.isSticky, refineSticky.isCompressed, refineSticky.isExpanded])

  // --- Sliding indicator pills (#870) --------------------------------------
  // Progressive enhancement: `indicatorsReady` only flips after mount, and the
  // CSS that blanks the fields' own hover/active backgrounds is scoped to that
  // class — so if this effect never runs, the bar looks exactly as it did
  // before the motion port instead of losing its states entirely.
  const barRef = useRef<HTMLFormElement>(null)
  // The form node's identity AS STATE: the node-binding effects below must
  // re-run whenever a render produces a fresh <form> — the hero portal
  // becoming ready (#951), or a live editor toggle of stickyFilter/heroBar
  // switching the render branch (#1220 item 2) — or their observers stay
  // bound to the detached old node until reload.
  const [barEl, setBarEl] = useState<HTMLFormElement | null>(null)
  const attachBar = useCallback((n: HTMLFormElement | null) => {
    barRef.current = n
    setBarEl((prev) => (prev === n ? prev : n))
  }, [])
  const hoverPillRef = useRef<HTMLSpanElement>(null)
  const activePillRef = useRef<HTMLSpanElement>(null)
  const [indicatorsReady, setIndicatorsReady] = useState(false)
  const [hasActiveField, setHasActiveField] = useState(false)

  // #1092: a popover closing between scroll events lifts the compression
  // freeze — re-evaluate so the pinned bar condenses without waiting for
  // the next scroll tick.
  useEffect(() => {
    if (!hasActiveField) refineSticky.refresh()
  }, [hasActiveField, refineSticky.refresh])

  // #1092 focus handoff: each morph state change hides one control class —
  // compressed hides fields/submit, full-bar and expanded hide the pill.
  // When the HIDDEN side holds focus, park it on the shell and then move it
  // to the surface that remains once the 330ms visibility sequence lands
  // (360 = sequence end + a settle frame; under reduced motion visibility
  // flips at once, so the handoff is immediate). Covers expand, collapse,
  // scroll-compress AND the scroll-back auto re-expand of a focused pill.
  // Layout effect on purpose: the park must land in the same frame the
  // outgoing control goes aria-hidden, before paint reaches the
  // accessibility tree. Skipped when focus already left the bar, and never
  // stolen back from elsewhere on the page.
  const refinePillRef = useRef<HTMLButtonElement>(null)
  // #1220 item 4: whether the compressed pill holds focus, tracked through
  // its own focus/blur, so an unpin (which unmounts the pill) can hand focus
  // to the in-flow bar instead of dropping it on body beside a fully
  // interactive bar. Browsers fire no blur when a focused node is removed;
  // an environment that does fires it with the node already on its way out
  // — the microtask re-check keeps the flag through the unmount and clears
  // it only for a real focus move (the pill is still connected then).
  const pillFocusedRef = useRef(false)
  const onPillFocus = useCallback(() => { pillFocusedRef.current = true }, [])
  const onPillBlur = useCallback((e: React.FocusEvent<HTMLButtonElement>) => {
    const pill = e.currentTarget
    queueMicrotask(() => { if (pill.isConnected) pillFocusedRef.current = false })
  }, [])
  useLayoutEffect(() => {
    if (!refineSticky.isSticky) {
      const active = deepActiveElement()
      const shell = refineSticky.shellRef.current
      // Two ways focus can be stranded by an un-pin: the pill held it (the
      // pill unmounts, focus drops to body), or the morph handoff below had
      // parked it on the shell and the un-pin landed inside the 360ms window
      // (the timer is cleared by this effect's cleanup, so nothing else moves
      // it off the outline-less shell). Never stolen from a control the
      // visitor moved to.
      const parkedOnShell = !!shell && active === shell
      if (!pillFocusedRef.current && !parkedOnShell) return
      pillFocusedRef.current = false
      if (shell && (!active || active === document.body || parkedOnShell)) {
        // #1846: an un-pin forced by the narrow-container swap lands the bar
        // in display:none, which cannot take focus — the trigger pill is the
        // visible successor there.
        const successor = narrow
          ? wrapperRef.current?.querySelector<HTMLElement>('.hfse-sbp-mobile-trigger')
          : shell.querySelector<HTMLElement>('.hfse-sbp-field-btn, .hfse-sbp-submit')
        successor?.focus({ preventScroll: true })
      }
      return
    }
    const shell = refineSticky.shellRef.current
    if (!shell) return
    const active = deepActiveElement()
    if (!shell.contains(active)) return
    const pill = refinePillRef.current
    const activeIsPill = !!pill && (active === pill || pill.contains(active as Node))
    const target = refineSticky.isCompressed ? (activeIsPill ? null : 'pill') : activeIsPill ? 'field' : null
    if (!target) return
    // Park focus on the shell RIGHT NOW: the outgoing control just went
    // aria-hidden/visibility-sequenced while still holding focus, and an
    // aria-hidden element must never keep it — not even for the morph
    // window. The shell (tabIndex -1) is the stable interim surface; the
    // timer below moves focus onto the incoming control once visible.
    shell.focus({ preventScroll: true })
    // Reduced motion kills the 330ms visibility sequence, so the surface to
    // focus is already there — hand off immediately instead of parking focus
    // on a hidden control for the full delay.
    const reducedMotion =
      typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
    const t = window.setTimeout(() => {
      const active = deepActiveElement()
      // The interim park (or a hidden control dropping focus to body) keeps
      // focus in/near the shell; anything else means the user moved on —
      // leave their focus alone.
      if (active && active !== document.body && !shell.contains(active)) return
      if (target === 'field') {
        // First FIELD BUTTON, not the Where input — its onFocus opens the
        // listbox, and expand must not auto-pop a popover (module parity:
        // .is-expanded only shows the fields). Submit is the fallback for a
        // bar with no button fields.
        shell.querySelector<HTMLElement>('.hfse-sbp-field-btn, .hfse-sbp-submit')?.focus()
      } else {
        refinePillRef.current?.focus()
      }
    }, reducedMotion ? 0 : 360)
    return () => window.clearTimeout(t)
  }, [refineSticky.isSticky, refineSticky.isExpanded, refineSticky.isCompressed, refineSticky.shellRef, narrow])

  // #1846, the hero twin of the un-pin handoff above. A narrow swap stands
  // the hero sticky down; its compressed pill then unmounts with the un-pin
  // and browsers move focus to body silently. The pill's own blur cannot be
  // the signal (the hero's exit blurs it before the unmount), so note at the
  // swap commit whether the pinned shell holds focus, and hand focus to the
  // trigger pill once the shell is down — never stolen from a control the
  // visitor moved to meanwhile.
  const heroNarrowHandoffRef = useRef(false)
  useLayoutEffect(() => {
    if (!narrow) {
      heroNarrowHandoffRef.current = false
      return
    }
    if (heroSticky.isSticky) {
      const shell = heroSticky.shellRef.current
      const active = deepActiveElement()
      heroNarrowHandoffRef.current = !!shell && !!active && shell.contains(active)
      return
    }
    if (!heroNarrowHandoffRef.current) return
    heroNarrowHandoffRef.current = false
    const active = deepActiveElement()
    if (!active || active === document.body) {
      wrapperRef.current?.querySelector<HTMLElement>('.hfse-sbp-mobile-trigger')?.focus({ preventScroll: true })
    }
  }, [heroSticky.isSticky, heroSticky.shellRef, narrow])

  useEffect(() => {
    const bar = barEl
    if (!bar) return
    setIndicatorsReady(true)
    // Deactivation is DEFERRED, activation immediate (r7 #936). Clicking from
    // field A to field B closes A on pointerdown (Radix outside-dismiss) but
    // opens B only on the following click — a real-click-length gap in which
    // a naive sync would drop the bar to inactive, collapse the submit to
    // icon-only, and hide the pill, then reverse it all. The toolkit never
    // round-trips through "nothing active" for exactly this reason: its
    // WHERE-blur defers the deactivate check 150ms and lets the next
    // activation win by re-checking identity when the timer fires
    // (searchbar-pro.js:3558-3569). Same shape here: an empty selector result
    // schedules the downgrade; a non-empty one cancels it and applies at once.
    let deactivateTimer: number | null = null
    const sync = () => {
      const open = bar.querySelector<HTMLElement>(ACTIVE_FIELD_SELECTOR)
      if (open) {
        if (deactivateTimer != null) {
          clearTimeout(deactivateTimer)
          deactivateTimer = null
        }
        applyPill(activePillRef.current, open, 'hfse-sbp-active-pill-visible')
        // Read from the SELECTOR, not applyPill's return: applyPill answers
        // "could the pill be placed?" and returns null for a missing ref or
        // an unlaid-out (0-wide) field. The submit's expand is a different
        // claim — "a field is open" — and the toolkit sets it from
        // `next !== null` before it ever touches the pill
        // (searchbar-pro.js:2464-2470). Coupling them would silently stop the
        // button expanding whenever the pill could not be measured.
        hasActiveFieldRef.current = true
        setHasActiveField(true)
      } else if (deactivateTimer == null) {
        deactivateTimer = window.setTimeout(() => {
          deactivateTimer = null
          const still = bar.querySelector<HTMLElement>(ACTIVE_FIELD_SELECTOR)
          applyPill(activePillRef.current, still, 'hfse-sbp-active-pill-visible')
          hasActiveFieldRef.current = !!still
          setHasActiveField(!!still)
        }, 150)
      }
    }
    sync()
    // The open field is owned by the child components, so there is no prop to
    // watch — observe the attributes they actually flip. `style` is
    // deliberately not in the filter: applyPill writes inline left/width, and
    // observing it would re-enter this callback on every write.
    const observer = new MutationObserver(sync)
    observer.observe(bar, { subtree: true, attributes: true, attributeFilter: ['data-state', 'class'] })
    // A resize relays out the fields; the pill must follow or it strands at
    // the old field's coordinates. The bar alone is not enough: it is
    // width-capped, so a label or CTA-count text change can re-flex the
    // fields WITHOUT resizing the bar — the reference observes every field
    // for exactly this (searchbar-pro.js:2406-2423). The field set is fixed
    // after mount, so a one-time observe pass covers it.
    // Resizes SNAP the pill instead of springing it: the reference disables
    // the transition for the duration of an observed resize and restores it
    // a frame later (searchbar-pro.js:2406-2423) — otherwise the pill chases
    // a reflow for its full 320ms (codex fresh pass).
    const snapSync = () => {
      const pills = [hoverPillRef.current, activePillRef.current]
      for (const p of pills) if (p) p.style.transition = 'none'
      sync()
      requestAnimationFrame(() => {
        for (const p of pills) if (p) p.style.transition = ''
      })
    }
    const resize = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(snapSync) : null
    if (resize) {
      resize.observe(bar)
      for (const el of Array.from(
        bar.querySelectorAll<HTMLElement>('.hfse-sbp-field, .hfse-sbp-field-btn, .hfse-sbp-submit'),
      )) {
        resize.observe(el)
      }
    }
    return () => {
      observer.disconnect()
      resize?.disconnect()
      if (deactivateTimer != null) clearTimeout(deactivateTimer)
    }
    // resetKey remounts every field node (Start Over / Clear all), which
    // would leave the ResizeObserver watching detached elements — re-running
    // the effect rebinds both observers onto the fresh nodes. The promoted
    // slot (#922) mounts/unmounts a field node the same way when its setting
    // flips (editor preview), so it re-binds too.
    // barEl: a branch switch or the hero portal flip remounts the FORM
    // itself — same rebind reasoning as resetKey, one level up (#1220 item 2).
  }, [barEl, state.resetKey, sb.barPromotedFilter])

  const onBarPointerOver = useCallback((e: React.PointerEvent) => {
    const target = e.target as HTMLElement | null
    const field = target?.closest?.('.hfse-sbp-field') as HTMLElement | null
    // The handler is bound to the bar, so anything reaching it is already in
    // the bar's React tree. The one exception is a portal: the field dialogs
    // render their content through one, and React bubbles portal events to the
    // logical parent even though the DOM node lives elsewhere. Excluding
    // dialog content is therefore the whole guard — a DOM containment check
    // would say the same thing while also being wrong for portals.
    if (!field || field.closest('.hr-dialog-content')) {
      // Same portal logic means entering an open dialog never fires the
      // bar's pointer-leave, so the hover class has to come off HERE — or
      // the pill reappears at its stale position when the dialog closes
      // (codex fresh pass).
      hoverPillRef.current?.classList.remove('hfse-sbp-hover-indicator-visible')
      return
    }
    applyPill(hoverPillRef.current, field, 'hfse-sbp-hover-indicator-visible')
  }, [])

  const onBarPointerLeave = useCallback(() => {
    hoverPillRef.current?.classList.remove('hfse-sbp-hover-indicator-visible')
  }, [])

  // --- Submit CTA label (#870 QA items 4 + 5) -------------------------------
  // Built locally from the same infinite query that paints the grid — a
  // second count request would only add staleness. The verb stays
  // `submitBtnText` so the existing setting keeps working.
  //
  // Composition (Eli decision, 2026-08-05 audit item 3): the default verb
  // reads "Search 7 homes"; a CUSTOM verb joins with a middot ("Book Now
  // · 7 homes") because gluing an arbitrary verb straight onto the noun
  // phrase is ungrammatical for most custom labels.
  //
  // `totalsReady` is the whole reason useFilterTotalsState exists: without it
  // the first paint reads "Search 0 homes" before snapping to the real number.
  const submitLabel = options.submitBtnText || 'Search'
  // HERO-ONLY (r8 #937, Eli): the module's initCtaCount() bails on refine
  // bars (searchbar-pro.js:4294-4303) — an inline explorer's grid and
  // results-header count already live-update, so a moving CTA there is
  // redundant noise. redirectOnSubmit is the same hero marker autoAdvance
  // keys on above.
  const showCount = !!sb.showResultCount && totalsReady && isRedirectMode(options)
  const countPhrase = `${totalItems.toLocaleString()} ${totalItems === 1 ? 'home' : 'homes'}`
  const ctaLabel = showCount
    ? (options.submitBtnText ? `${submitLabel} · ${countPhrase}` : `${submitLabel} ${countPhrase}`)
    : submitLabel

  // Measured expanded width. The collapsed button is `overflow: hidden` and the
  // label span is `width: 0`, so a long count label ("Search 1,234 homes")
  // would clip against the stock 130px pill. This measurement subsystem is a
  // WIDGET INVENTION, not a port: the toolkit's expanded submit is a
  // hardcoded 130px (searchbar-pro.css:698-702) because its label never
  // carries a count. Ours does, so we measure the text on a canvas (the span
  // has no width to read while collapsed) and publish the result as an inline
  // custom property the expanded rule consumes. Kept inline on the element
  // (never in the stylesheet) so it stays a per-render measurement and cannot
  // collide with the settings lane's emitted tokens.
  const submitRef = useRef<HTMLButtonElement>(null)
  const submitLabelRef = useRef<HTMLSpanElement>(null)
  const measureCtx = useRef<CanvasRenderingContext2D | null | undefined>(undefined)

  const sizeSubmit = useCallback((text: string | null) => {
    const btn = submitRef.current
    if (!btn) return
    if (text === null) {
      // Removing (not zeroing) falls back to the CSS 130px.
      btn.style.removeProperty('--hfse-sbp-cta-w')
      return
    }
    const labelEl = submitLabelRef.current
    if (!labelEl) return
    if (measureCtx.current === undefined) {
      measureCtx.current = document.createElement('canvas').getContext('2d')
    }
    const ctx = measureCtx.current
    const cs = window.getComputedStyle(labelEl)
    let textW: number
    if (ctx) {
      ctx.font = cs.font || `${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}`
      textW = ctx.measureText(text).width
    } else {
      // No 2d context (some locked-down webviews). offsetWidth reads 0 on a
      // width:0 span, but scrollWidth still reports the overflowing content.
      textW = labelEl.scrollWidth
    }
    const icon = btn.querySelector('.hfse-sbp-submit-icon')
    // getBoundingClientRect, not offsetWidth: an inline SVG has no offsetWidth
    // and `undefined` would NaN the sum.
    const iconW = icon ? Math.ceil(icon.getBoundingClientRect().width) : 0
    // Real text metrics but a zero icon means the bar is display:none
    // (mobile viewport, hidden tab): canvas measures without layout, rects
    // do not. A width pinned from that state is short by the icon, so fall
    // back to the CSS 130px and let the resize listener re-measure when the
    // bar is actually visible (2026-08-05 audit). The textW term keeps this
    // out of happy-dom's way, where BOTH measure 0.
    if (icon && iconW === 0 && textW > 0) {
      btn.style.removeProperty('--hfse-sbp-cta-w')
      return
    }
    // Gap + horizontal padding restate the expanded rule in explorer.css;
    // searchbar-motion-css.test.ts asserts the stylesheet against these
    // exported constants so the two cannot drift silently.
    const w = Math.ceil(textW) + iconW + CTA_GAP_PX + CTA_PAD_X_PX * 2
    if (!isFinite(w) || w <= 0) return
    btn.style.setProperty('--hfse-sbp-cta-w', `${Math.max(130, w)}px`)
  }, [])

  useEffect(() => {
    // Measure whatever the button will actually render, count or not. Gating
    // on showCount assumed the no-count label is always the stock "Search",
    // which fits the 130px fallback — but `submitBtnText` is a site setting,
    // and a site with a custom label AND the count switched off got no
    // measurement at all: the expanded pill stayed 130px with `overflow:
    // hidden`, leaving ~56px of inner width for a label like "Check
    // Availability" that needs ~159px. Short labels are unaffected because
    // sizeSubmit already floors at 130px.
    sizeSubmit(ctaLabel)
    // Re-measure on viewport changes: crossing the 933px breakpoint mounts
    // the bar out of display:none (where the icon measured 0), and rotation
    // or responsive host typography can change the label's computed font
    // (2026-08-05 audit). rAF-coalesced so a drag resize measures once per
    // frame at most.
    let resizeRaf = 0
    const onResize = () => {
      if (resizeRaf) return
      resizeRaf = requestAnimationFrame(() => {
        resizeRaf = 0
        sizeSubmit(ctaLabel)
      })
    }
    window.addEventListener('resize', onResize)
    // Fallback-face metrics are narrower than the real font's, so a width
    // pinned before webfonts settle can clip. `document.fonts` is
    // feature-detected because it is absent in some embedded webviews and
    // this widget ships into arbitrary customer sites.
    const fonts = (document as Document & { fonts?: { ready?: Promise<unknown> } }).fonts
    let cancelled = false
    if (fonts?.ready && typeof fonts.ready.then === 'function') {
      fonts.ready.then(() => { if (!cancelled) sizeSubmit(ctaLabel) })
    }
    return () => {
      cancelled = true
      window.removeEventListener('resize', onResize)
      if (resizeRaf) cancelAnimationFrame(resizeRaf)
    }
    // barEl: a form remount replaces the submit button; the width var must
    // be re-pinned on the fresh node even when ctaLabel didn't change.
    // narrow (#1846): while the bar is display:none the icon measures 0 and
    // sizeSubmit drops the var; the bar coming back fires no window resize,
    // so the flip itself re-pins it.
  }, [ctaLabel, sizeSubmit, barEl, narrow])

  // resetKey remounts clear each dialog's local edit state on Start Over /
  // Clear all (legacy parity).
  // `popover` is DESKTOP-ONLY: these bar instances anchor their dialogs under
  // the bar (toolkit popover shape, QA item 1). The mobile sheet's staged
  // instances below deliberately don't get it — their triggers sit inside the
  // sheet, and the anchoring hook additionally requires a laid-out
  // `.hfse-sbp-bar` ancestor, so the prop split and the DOM gate agree.
  const renderPrimaryField = (field: string) => {
    switch (field) {
      case 'where': return <ProWhereField key={`where-${state.resetKey}`} onDestinationPick={() => advanceFrom('where')} />
      case 'date': return <DateFilter key={`date-${state.resetKey}`} pro popover advanceSignal={signalFor('date')} onApplyAdvance={() => advanceFrom('date')} />
      case 'guests': return <GuestFilter key={`guest-${state.resetKey}`} pro popover advanceSignal={signalFor('guests')} onApplyAdvance={() => advanceFrom('guests')} />
      case 'additional-filters': return <AdditionalFilters key={`addl-${state.resetKey}`} pro popover advanceSignal={signalFor('additional-filters')} />
      case 'promoted-bedrooms': return <BedroomsField key={`bed-${state.resetKey}`} advanceSignal={signalFor('promoted-bedrooms')} onApplyAdvance={() => advanceFrom('bedrooms')} />
      default: return null
    }
  }

  const handleSubmit = (e: React.FormEvent | React.MouseEvent) => {
    e.preventDefault()
    submitSearch()
    setMobileOpen(false)
    // Refine sticky: submit returns the pinned bar to the pill (#1092 ask —
    // the results refresh live under it, so the expanded bar has nothing
    // left to do). No-op unless pinned and expanded.
    refineSticky.collapse()
  }

  // The desktop bar is extracted so the hero layout can wrap it in the
  // module's sticky anatomy (sentinel/shell/spacer/backdrop,
  // search-bar.php:312/337/1415/1417) without changing the default
  // (non-hero) DOM by a single node.
  const desktopBar = (
      <form
        method="get"
        noValidate
        className={`hfse-sbp-bar${compactBar.compact ? ' hfse-sbp-bar-compact' : ''}${indicatorsReady ? ' hfse-sbp-bar-indicators' : ''}${hasActiveField ? ' hfse-sbp-bar-has-active' : ''}${heroCompressed ? ' hfse-sbp-bar-hero-compressed' : ''}${refineCompressed ? ' hfse-sbp-bar-refine-compressed' : ''}`}
        role="search"
        ref={attachBar}
        onSubmit={handleSubmit}
        onPointerOver={onBarPointerOver}
        onPointerLeave={onBarPointerLeave}
      >
        <span className="hfse-sbp-hover-indicator" ref={hoverPillRef} aria-hidden />
        <span className="hfse-sbp-active-pill" ref={activePillRef} aria-hidden />
        {/* Compressed pill (module search-bar.php:362-366): summary text +
            accent search icon; the bar's fields/dividers/submit are hidden by
            the -hero-compressed class while this renders. Clicking expands
            back to the full bar in the top-attached panel. */}
        {heroCompressed && (
          <button type="button" className="hfse-sbp-hero-compressed" onClick={heroSticky.expand}>
            <span className="hfse-sbp-hero-compressed-summary">{compressedSummary}</span>
            <span className="hfse-sbp-hero-compressed-icon" aria-hidden>{searchIcon}</span>
          </button>
        )}
        {/* Refine compressed pill (#1092): unlike the hero's (a conditional
            render into a display:none'd bar), this one stays MOUNTED for the
            whole pinned stretch as an absolute overlay — the module keeps
            fields AND pill rendered and sequences their opacity so neither
            fade ever re-layouts (searchbar-pro.debug.css:3741-3755). Inner
            look reuses the hero pill classes on purpose: the module styles
            ONE __compressed pill for both branches; the -hero- in those
            names is historical (#934 landed first). While invisible it is
            inert to keyboard/AT — the module leaves it focusable at
            opacity 0; kept out of the tab order here instead. */}
        {refineSticky.isSticky && (
          <button
            type="button"
            ref={refinePillRef}
            className="hfse-sbp-hero-compressed hfse-sbp-refine-pill"
            onClick={refineSticky.expand}
            onFocus={onPillFocus}
            onBlur={onPillBlur}
            tabIndex={refineCompressed ? 0 : -1}
            aria-hidden={!refineCompressed}
          >
            <span className="hfse-sbp-hero-compressed-summary">{compressedSummary}</span>
            <span className="hfse-sbp-hero-compressed-icon" aria-hidden>{searchIcon}</span>
          </button>
        )}
        {barFields.map((field, i) => (
          <span key={`wrap-${field}`} className="hfse-sbp-bar-slot">
            {i > 0 && <span className="hfse-sbp-divider" aria-hidden />}
            {renderPrimaryField(field)}
          </span>
        ))}
        {/* Collapsed the button is an icon-only circle, so the visible label is
            width:0/opacity:0 — the accessible name has to come from the
            attribute. Same expression feeds both, so there is no restore path
            to keep in sync. */}
        <button
          type="submit"
          className="hfse-sbp-submit"
          aria-label={ctaLabel}
          onClick={handleSubmit}
          ref={submitRef}
        >
          <span className="hfse-sbp-submit-icon">{searchIcon}</span>
          {/* Always mounted: the collapse has nothing to clip otherwise, and
              the measurement has no node to read a computed font from. */}
          <span className="hfse-sbp-submit-label" ref={submitLabelRef}>{ctaLabel}</span>
        </button>
      </form>
  )

  // #951: the two fixed-position pieces — shell + backdrop — render into the
  // re-parented portal container (hero elevation hook above). The sentinel and
  // spacer stay in the widget tree: the sentinel drives the observer against
  // the page scroll, and the spacer holds the vacated flow height.
  const heroAnatomy = heroStickyOn ? (
    <>
      <div
        ref={heroSticky.shellRef}
        className={`hfse-sbp-hero-shell${heroSticky.isSticky ? ' hfse-sbp-hero-shell-sticky' : ''}${heroSticky.isExpanded ? ' hfse-sbp-hero-shell-expanded' : ''}`}
        // Clears a host site's fixed / reveal-on-scroll-up header: the
        // pinned states read this var (compressed calc(16px + var),
        // expanded var directly). Absent at the default 0 so the CSS
        // fallback keeps module parity byte-for-byte.
        style={(options.search?.heroStickyTopPx ?? 0) > 0
          ? ({ '--hfse-sbp-hero-top': `${options.search!.heroStickyTopPx}px` } as React.CSSProperties)
          : undefined}
      >
        {desktopBar}
      </div>
      {heroSticky.isSticky && (
        <div
          className={`hfse-sbp-hero-backdrop${heroSticky.isExpanded ? ' hfse-sbp-hero-backdrop-visible' : ''}`}
          onClick={heroSticky.collapse}
          aria-hidden
        />
      )}
    </>
  ) : null

  // #1259: the refine twin — shell + backdrop (the two fixed-position pieces)
  // render into their own re-parented portal container; the sentinel and
  // spacer stay in the slot as the hook's geometry sources.
  const refineAnatomy = refineStickyOn ? (
    <>
      <div
        ref={refineSticky.shellRef}
        // Programmatic focus target only — the interim park between the
        // outgoing and incoming control of the morph (never tabbable).
        tabIndex={-1}
        className={`hfse-sbp-refine-shell${refineSticky.isSticky ? ' hfse-sbp-refine-shell-sticky' : ''}${refineSticky.isExpanded ? ' hfse-sbp-refine-shell-expanded' : ''}`}
        // search.heroStickyTopPx serves BOTH pinned modes (#1092: native
        // offset so the pinned bar clears a fixed site header without
        // the Duda shadow-root shim). Absent at the default 0 so the CSS
        // 12px fallback keeps module parity byte-for-byte.
        style={(options.search?.heroStickyTopPx ?? 0) > 0
          ? ({ '--hfse-sbp-refine-top': `${options.search!.heroStickyTopPx}px` } as React.CSSProperties)
          : undefined}
      >
        {desktopBar}
      </div>
      {refineSticky.isSticky && (
        <div
          className={`hfse-sbp-refine-backdrop${refineSticky.isExpanded ? ' hfse-sbp-refine-backdrop-visible' : ''}`}
          onClick={refineSticky.collapse}
          aria-hidden
        />
      )}
    </>
  ) : null

  return (
    <div className={`hfse-filter-bar hfse-sbp${narrow ? ' hfse-sbp-narrow' : ''}`} ref={wrapperRef}>
      {/* Desktop pill bar (≥933px); either sticky layout wraps it in the
          module's anatomy — sentinel above, shell around, spacer + backdrop
          after (hero detaches to the viewport pill, refine pins in place).
          With neither sticky on, the bare form renders, byte-identical to
          r8. */}
      {heroStickyOn ? (
        <>
          <div className="hfse-sbp-hero-sentinel" ref={heroSticky.sentinelRef} aria-hidden />
          {/* #951 anchor: the anatomy's flow position. display:contents keeps
              the shell a direct flex item of .hfse-filter-bar whether it
              renders here pre-mount or arrives inside the re-parented portal
              container. */}
          <div className="hfse-sbp-hero-anchor" ref={heroElevation.anchorRef} style={{ display: 'contents' }}>
            {!heroElevation.portalReady && heroAnatomy}
          </div>
          {heroElevation.portalReady && heroElevation.portalContainer && createPortal(heroAnatomy, heroElevation.portalContainer)}
          <div className="hfse-sbp-hero-spacer" style={{ height: heroSticky.spacerHeight }} aria-hidden />
        </>
      ) : refineStickyOn ? (
        /* Refine sticky anatomy (#1092): same sentinel/shell/spacer/
           backdrop shape as the hero above, but the shell pins IN PLACE
           over the column (left/width written by useRefineSticky) and the
           backdrop is a transparent click-catcher — no dimming for an
           in-place bar (module css:3688-3693). The slot wrapper keeps this
           whole anatomy ONE flex item of .hfse-filter-bar: bare children
           would each earn the container's 20px gap, and the gap count would
           CHANGE the moment the shell goes fixed (out of flow) — a visible
           jump of everything below on every pin/unpin. */
        <div className="hfse-sbp-refine-slot">
          <div className="hfse-sbp-refine-sentinel" ref={refineSticky.sentinelRef} aria-hidden />
          {/* #1259 anchor: the anatomy's flow position between sentinel and
              spacer. display:contents keeps the shell a direct child of the
              slot whether it renders here pre-mount or arrives inside the
              re-parented portal container, so the slot's box and the gap
              accounting above are untouched. */}
          <div className="hfse-sbp-refine-anchor" ref={refineElevation.anchorRef} style={{ display: 'contents' }}>
            {!refineElevation.portalReady && refineAnatomy}
          </div>
          {refineElevation.portalReady && refineElevation.portalContainer && createPortal(refineAnatomy, refineElevation.portalContainer)}
          <div
            className="hfse-sbp-refine-spacer"
            ref={refineSticky.spacerRef}
            style={{ height: refineSticky.spacerHeight }}
            aria-hidden
          />
        </div>
      ) : desktopBar}

      {/* Mobile trigger pill + staged search sheet (<933px) */}
      <Dialog open={mobileOpen} onOpenChange={(o) => { setMobileOpen(o); if (o) setSheetStage(sheetStageList[0] ?? '') }}>
        <DialogTrigger asChild>
          <button type="button" className="hfse-sbp-mobile-trigger">
            <span className="hfse-sbp-mobile-trigger-icon">{searchIcon}</span>
            <span className="hfse-sbp-mobile-trigger-lines">
              <span className="hfse-sbp-mobile-trigger-primary">{mobileSummary.primary}</span>
              <span className="hfse-sbp-mobile-trigger-secondary">{mobileSummary.secondary}</span>
            </span>
          </button>
        </DialogTrigger>
        <DialogContent
          className="hfse-sbp-mobile-sheet"
          overlayClassName="hfse-sbp-mobile-overlay"
          // The generic dialog wrapper pads 20px and the 100vw sheet only
          // OVERFLOWS it back toward full-bleed — centering math then makes
          // the sheet's true width vary run to run (measured 318-388px at a
          // 390px viewport), which intermittently clipped the inline
          // calendar's Saturday column. The module sheet is an edge-to-edge
          // takeover; zero the wrapper so this one is too, deterministically.
          wrapperStyle={{ padding: 0 }}
          elevateDom
          cssString={cssString}
          title="Search"
          // #925: Radix auto-focuses the first focusable in the content — the
          // Where input — which raises the phone keyboard over the sheet the
          // visitor just opened. The module never calls .focus(); the sheet
          // must open with no field focused (probe falsified the tracker's
          // "already satisfied" claim: whereInputFocused=true before this).
          // Focus still has to LAND inside the dialog, though — cancelling
          // alone strands it on the aria-hidden trigger behind the sheet. The
          // close button takes it: a button raises no keyboard, so #925 holds.
          onOpenAutoFocus={(e) => {
            e.preventDefault()
            ;(e.currentTarget as HTMLElement | null)
              ?.querySelector<HTMLElement>('.hr-dialog-close')
              ?.focus({ preventScroll: true })
          }}
        >
          <div className="hk-modal-header">
            {/* Module Back: circular icon button in the sheet HEADER (mirror
                of the close X), hidden on the first stage — commits nothing
                extra here since sheet stages live-apply. */}
            {sheetStageIdx > 0 && (
              <button
                type="button"
                className="hfse-sbp-mobile-back-btn"
                aria-label="Back"
                onClick={() => setSheetStage(sheetStageList[sheetStageIdx - 1])}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
              </button>
            )}
            <span className="hk-h4">Search</span>
          </div>
          <div className="hk-modal-body hfse-sbp-mobile-body">
            {primaryFields.includes('where') && (
              <div className={`hfse-sbp-mobile-stage${sheetStage === 'where' ? ' hfse-sbp-stage-active' : ''}`}>
                <ProWhereField
                  key={`m-where-${state.resetKey}`}
                  inline
                  sheet
                  sheetActive={sheetStage === 'where'}
                  onSheetActivate={() => setSheetStage('where')}
                  onDestinationPick={() => advanceSheetFrom('where')}
                />
              </div>
            )}
            {primaryFields.includes('date') && (
              <div className={`hfse-sbp-mobile-stage${sheetStage === 'date' ? ' hfse-sbp-stage-active' : ''}`}>
                <DateFilter key={`m-date-${state.resetKey}`} pro sheet sheetActive={sheetStage === 'date'} onSheetActivate={() => setSheetStage('date')} />
              </div>
            )}
            {primaryFields.includes('guests') && (
              <div className={`hfse-sbp-mobile-stage${sheetStage === 'guests' ? ' hfse-sbp-stage-active' : ''}`}>
                <GuestFilter key={`m-guest-${state.resetKey}`} pro sheet sheetActive={sheetStage === 'guests'} onSheetActivate={() => setSheetStage('guests')} />
              </div>
            )}
            {primaryFields.includes('additional-filters') && (
              <div className={`hfse-sbp-mobile-stage${sheetStage === 'additional-filters' ? ' hfse-sbp-stage-active' : ''}`}>
                <AdditionalFilters key={`m-addl-${state.resetKey}`} pro sheet sheetActive={sheetStage === 'additional-filters'} onSheetActivate={() => setSheetStage('additional-filters')} />
              </div>
            )}
          </div>
          <div className="hk-modal-footer hk-flex">
            {/* Module Clear-all jumps back to WHERE after resetting
                (searchbar-pro.js:5484-5510 ends in setActiveStage('where')).
                resetFilters bumps resetKey, which remounts every stage
                component — so unlike the module, the datepicker's internal
                draft cannot survive a clear (its mobile Clear-all skips
                datePicker.clearDates(); ours gets the reset for free). */}
            <button
              type="button"
              className="hk-button hk-button-link hfse-modal-reset-button hfse-show-always"
              onClick={() => { resetFilters(); setSheetStage(sheetStageList[0] ?? '') }}
            >
              {labels.clearAll}
            </button>
            {/* Module footer-actions pair: outlined Next (manual stage
                advance, hidden on the last stage) beside Search. */}
            <div className="hfse-sbp-mobile-footer-actions">
              {sheetNextStage && (
                <button
                  type="button"
                  className="hk-button hfse-sbp-mobile-next-btn"
                  onClick={() => setSheetStage(sheetNextStage)}
                >
                  {labels.next}
                </button>
              )}
              {/* The sheet CTA never collapses and is always labelled — the
                  morph is desktop-only in the toolkit too, whose mobile action
                  button is a plain content-sized pill
                  (searchbar-pro.css:2836-2880, `__mobile-action`). It does
                  carry the same count. */}
              <button
                type="button"
                className="hk-button hk-button-small hfse-sbp-mobile-search-btn"
                onClick={handleSubmit}
              >
                {searchIcon}
                {ctaLabel}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Directly under the bar, above the quick-filter chips — it refines the
          WHEN search the bar just above it (toolkit search-bar.php:1200-1208).
          The component decides for itself whether there is anything to show. */}
      <FlexAltDates />

      {sb.chips.enabled && <QuickChips />}

      {/* Hero (#934): the module's results header exists ONLY in refine mode —
          a standalone hero has no results on the page to count, sort, or pair
          with a map, so the row is hidden by construction, not by setting. */}
      {!isHeroBar(options) && withSecondaryRow && <SecondaryRow totalItems={totalItems} pro />}
    </div>
  )
}
